import { customElement, property } from "lit/decorators.js";
import { css, html, LitElement, PropertyValues, TemplateResult } from "lit";
import { initTooltips } from "@/utilities";
import { searchQueryState } from "@/state/SearchQuery";
import { StateController } from "@dodona/lit-state";
import { DodonaElement } from "@/components/meta/dodona_element";
import { Tooltip } from "bootstrap";

/**
 * This is a very simple clickable component
 * It sets a given query param to a given value when clicked
 *
 * @element d-filter-button
 *
 * @prop {string} param - The searchQuery param to be used
 * @prop {string} value - The value to be used in the searchQuery
 * @prop {boolean} multi - Should be specified in case of array query params
 *
 * @slot - This contains the clickable button content
 */
@customElement("d-filter-button")
export class FilterButton extends LitElement {
    @property({ type: String })
    param: string = "";

    @property({ type: String })
    value: string = "";

    @property({ type: Boolean })
    multi = false;

    state = new StateController(this);

    static styles = css`
        :host {
            cursor: pointer;
        }
    `;

    connectedCallback(): void {
        super.connectedCallback();

        // Make the custom element keyboard-focusable and button-like
        if (!this.hasAttribute("role")) this.setAttribute("role", "button");
        if (this.tabIndex < 0) this.tabIndex = 0;

        this.addEventListener("click", this.onClick);
        this.addEventListener("keydown", this.onKeyDown);
    }

    disconnectedCallback(): void {
        this.removeEventListener("click", this.onClick);
        this.removeEventListener("keydown", this.onKeyDown);
        super.disconnectedCallback();
    }

    protected updated(): void {
        // Only set a fallback aria-label when there is no visible text label.
        // (If the slotted content has text, the accessible name will come from it.)
        const hasTextLabel = (this.textContent ?? "").trim().length > 0;
        if (!hasTextLabel && !this.hasAttribute("aria-label")) {
            this.setAttribute("aria-label", `${this.param}=${this.value}`);
        }
    }

    private readonly onClick = (e: Event): void => {
        this.addFilter(e);
    };

    private readonly onKeyDown = (e: KeyboardEvent): void => {
        // Match native button behavior: Space/Enter activates.
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            this.addFilter(e);
        }
    };

    addFilter(e: Event): void {
        e.preventDefault();
        e.stopPropagation();
        if (this.multi) {
            const selected = new Set(searchQueryState.arrayQueryParams.get(this.param));
            selected.add(this.value);
            searchQueryState.arrayQueryParams.set(this.param, Array.from(selected));
        } else {
            searchQueryState.queryParams.set(this.param, this.value);
        }
    }

    render(): TemplateResult {
        return html`<slot></slot>`;
    }
}

/**
 * This is a clickable filter icon
 * When clicked it sets the given query param to the given value (defaults to 'filter')
 *
 * @element d-filter-icon
 *
 * @prop {string} param - The searchQuery param to be used (defaults to 'filter')
 * @prop {string} value - The value to be used in the searchQuery
 * @prop {string} iconTitle - The title text that should be displayed when hovering the icon
 */
@customElement("d-filter-icon")
export class FilterIcon extends DodonaElement {
    @property({ type: String })
    param: string = "filter";

    @property({ type: String })
    value: string = "";

    @property({ type: String, attribute: "icon-title" })
    iconTitle: string = "";

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);
        initTooltips(this);
    }

    disconnectedCallback(): void {
        this.hideTooltips();
        super.disconnectedCallback();
    }

    private hideTooltips(): void {
        const buttons = this.renderRoot.querySelectorAll<HTMLButtonElement>("[data-bs-toggle='tooltip']");
        buttons.forEach((btn) => {
            Tooltip.getInstance(btn)?.hide();
        });
    }

    render(): TemplateResult {
        return html`
            <d-filter-button
                .param=${this.param}
                .value=${this.value}
                aria-label="${this.iconTitle}"
                title="${this.iconTitle}"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
            >
                <i class="mdi mdi-filter-outline mdi-18 filter-icon" aria-hidden="true"></i>
            </d-filter-button>
        `;
    }
}
